import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { gsap, Power1 } from 'gsap';

import { getStarsSteps } from '@/pages/freePlay/Helpers';
import { AchievementProgressProps } from '@/pages/freePlay/types';

import Tooltip, { TooltipColors } from '@/components/tooltip/Tooltip';

const StarProgress = ({
	total,
	earned,
	previousEarned = 0,
	withAnimation,
	showAnimation,
	onAnimationComplete,
	tooltipOpen,
}: AchievementProgressProps) => {
	const { t } = useTranslation();
	const fillRef = useRef(null);
	const circleRef = useRef(null);
	const earnedRef = useRef<HTMLSpanElement>(null);

	const localTooltipAnchorId = useMemo(
		() => `star-progress-anchor-${Math.random().toString(36).slice(2, 10)}`,
		[],
	);
	const tooltipId = useMemo(
		() => `star-progress-tooltip-${Math.random().toString(36).slice(2, 10)}`,
		[],
	);

	const anchorId = localTooltipAnchorId;
	const tooltipLabel = useMemo(
		() => t('free_play.category.stars_needed', { count: total - earned }),
		[t, total, earned],
	);

	const steps = getStarsSteps(total);
	const earnedPercentage = Math.min(earned / total, 1) * 100;

	const setStartPointAnimation = (previousEarned: number) => {
		const previousEarnedPercentage = Math.min(previousEarned / total, 1) * 100;
		gsap.set(fillRef.current, { width: `${previousEarnedPercentage}%` });
		gsap.set(circleRef.current, { left: `${previousEarnedPercentage}%` });
		if (earnedRef.current) {
			earnedRef.current.textContent = Math.floor(previousEarned)?.toString();
		}
	};

	const animateProgress = (earned: number, previousEarned = 0) => {
		const earnedPercentage = Math.min(earned / total, 1) * 100;
		gsap.to(fillRef.current, {
			width: `${earnedPercentage}%`,
			duration: 1,
			delay: 0.5,
			ease: Power1.easeInOut,
		});
		gsap.to(circleRef.current, {
			left: `${earnedPercentage}%`,
			duration: 1,
			delay: 0.5,
			ease: Power1.easeInOut,
		});
		gsap.to(
			{ value: previousEarned },
			{
				value: earned,
				duration: 1,
				delay: 0.5,
				ease: Power1.easeInOut,
				onUpdate: function () {
					if (earnedRef.current) {
						earnedRef.current.textContent = Math.floor(
							this.targets()[0].value,
						)?.toString();
					}
				},
				onComplete: function () {
					onAnimationComplete && onAnimationComplete();
				},
			},
		);
	};

	useEffect(() => {
		if (withAnimation) {
			setStartPointAnimation(previousEarned);
		}
	}, [previousEarned, withAnimation]);

	useEffect(() => {
		if (withAnimation && showAnimation) {
			animateProgress(earned, previousEarned);
		}
	}, [showAnimation, earned, withAnimation]);

	return (
		<div className="star-progress">
			<div className="star-progress__bar">
				<div
					className="star-progress__fill"
					style={{ width: `${earnedPercentage}%` }}
					ref={fillRef}
				/>
				<div className="star-progress__dots">
					{steps.map((step) => (
						<span
							key={step}
							className={classNames('star-progress__dot', step <= earned && 'filled')}
						/>
					))}
				</div>
				<div
					id={anchorId}
					className="star-progress__circle"
					style={{ left: `${earnedPercentage}%` }}
					ref={circleRef}
					role="img"
					aria-label={tooltipLabel}
					tabIndex={0}
				>
					<span className="icon icon-star-filled" />
					<Tooltip
						id={tooltipId}
						title={tooltipLabel}
						anchor={anchorId}
						place="bottom"
						color={TooltipColors.DARK}
						className="star-progress__tooltip"
						isOpen={tooltipOpen}
					/>
				</div>
			</div>
			<div className="star-progress__numbers">
				<span
					className={classNames(
						'star-progress__earned',
						earned === 0 && 'star-progress__earned--light',
					)}
				>
					<span ref={earnedRef}>{earned}</span>/
				</span>
				<span className="star-progress__total">{total}</span>
			</div>
		</div>
	);
};

export default StarProgress;
